import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import "@fontsource/dm-serif-display/400.css";
import "@fontsource/dm-serif-display/400-italic.css";
import "@fontsource/fira-sans/300.css";
import "@fontsource/fira-sans/400.css";
import "@fontsource/fira-sans/500.css";
import "@fontsource/fira-sans/600.css";
import { MagazineHeader } from "@/components/MagazineHeader";
import { MagazineFooter } from "@/components/MagazineFooter";
import { CookieBanner } from "@/components/CookieBanner";
import { I18nProvider } from "@/components/I18nProvider";
import "@/lib/i18n";
import { getSettingText, SETTING_OG_GLOBAL_IMAGE } from "@/lib/app-settings";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  loader: async () => {
    try {
      const manual = await getSettingText(SETTING_OG_GLOBAL_IMAGE, null);
      return { ogImage: (manual && manual.trim()) || "https://omselected.art/og-cover.jpg" };
    } catch {
      return { ogImage: "https://omselected.art/og-cover.jpg" };
    }
  },
  head: ({ loaderData }) => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "OM Selected, Kuratiertes Magazin für Fotografie" },
      { name: "description", content: "Ein kuratiertes Magazin für Fotografie. Selected Works, Bild des Tages und ausgewählte Fotografen für Kunden, die das Besondere suchen." },
      { name: "author", content: "OM Selected" },
      { name: "theme-color", content: "#0a0a0a" },
      { name: "google-site-verification", content: "mgUlx5SOUOd2IhoHb0ba84E3RXEvZk_mIhg3CTBiMFM" },
      { property: "og:title", content: "OM Selected, Kuratiertes Magazin für Fotografie" },
      { property: "og:description", content: "Ein kuratiertes Magazin für Fotografie. Selected Works, Bild des Tages und ausgewählte Fotografen für Kunden, die das Besondere suchen." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@OMSelected" },
      { property: "og:image", content: loaderData?.ogImage ?? "https://omselected.art/og-cover.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:alt", content: "OM Selected, Kuratiertes Magazin für Fotografie" },
      { name: "twitter:image", content: loaderData?.ogImage ?? "https://omselected.art/og-cover.jpg" },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      {
        rel: "icon",
        type: "image/svg+xml",
        href:
          "data:image/svg+xml;utf8," +
          encodeURIComponent(
            `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" fill="#0a0a0a"/><text x="50%" y="50%" dy=".06em" text-anchor="middle" dominant-baseline="middle" font-family="Georgia, 'Times New Roman', serif" font-size="30" font-weight="500" fill="#fafafa">OM</text></svg>`
          ),
      },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/om-icon-192.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/om-icon-512.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/site.webmanifest" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "OM Selected",
          url: "https://omselected.art",
          description: "Kuratiertes Magazin für Fotografie. Ausgewählte Fotografen aus DACH für Portraits, Hochzeit, Produkt und freie Arbeiten.",
          inLanguage: "de-DE",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://omselected.art/photographers?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "OM Selected",
          url: "https://omselected.art",
          logo: "https://omselected.art/om-icon-512.png",
          description: "Kuratiertes Magazin für Fotografie aus DACH.",
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <MagazineHeader />
        <main>
          <Outlet />
        </main>
        <MagazineFooter />
        <CookieBanner />
      </I18nProvider>
    </QueryClientProvider>
  );
}
